import type { FC, ReactNode } from 'react'
import type { Patient } from '@/lib/clinic/types'

import { useMemo, useState } from 'react'
import { Command as CommandPrimitive } from 'cmdk'
import { ArrowRight, CalendarPlus, FilePlus2, Search, Stethoscope, UserPlus, UserRound } from 'lucide-react'
import { useClinic } from '@/hooks/use-clinic'
import { searchPatients } from '@/lib/clinic/search'
import { ageGenderLabel, formatPhone } from '@/lib/clinic/format'
import { formatDate, relativeDay, toDayKey } from '@/lib/clinic/datetime'
import { EMPTY_SUMMARY } from '@/lib/clinic/selectors'
import { Button } from '@/components/ui/button'
import PatientAvatar from '@/components/common/patient-avatar'

import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogTitle
} from '@/components/ui/dialog'

export type PickerMode = 'open' | 'consultation' | 'prescription' | 'follow-up'

interface PatientSearchDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    mode: PickerMode
    onSelect: (patient: Patient, mode: PickerMode) => void
    onRegister: (query: string) => void
}

const MODE_COPY: Record<PickerMode, { title: string, description: string, action: string, icon: typeof Search }> = {
    open: {
        title: 'Find a patient',
        description: 'Search by name, phone number or patient ID.',
        action: 'Open',
        icon: UserRound
    },
    consultation: {
        title: 'Start consultation',
        description: 'Choose the patient. Their details fill in automatically.',
        action: 'Start',
        icon: Stethoscope
    },
    prescription: {
        title: 'New prescription',
        description: 'Choose the patient for this prescription.',
        action: 'Select',
        icon: FilePlus2
    },
    'follow-up': {
        title: 'Add follow-up',
        description: 'Choose the patient to schedule a follow-up for.',
        action: 'Select',
        icon: CalendarPlus
    }
}

interface SearchPanelProps extends Omit<PatientSearchDialogProps, 'open' | 'onOpenChange'> {}

const SearchPanel: FC<SearchPanelProps> = ({ mode, onSelect, onRegister }): ReactNode => {
    const { data, index, today } = useClinic()
    const [query, setQuery] = useState('')
    const copy = MODE_COPY[mode]

    const results = useMemo(() => {
        if (query.trim()) return searchPatients(data.patients, query, 20)

        return [...data.patients]
            .sort((a, b) => {
                const lastA = index.summaries.get(a.id)?.lastConsultationAt ?? a.createdAt
                const lastB = index.summaries.get(b.id)?.lastConsultationAt ?? b.createdAt

                return lastB.localeCompare(lastA)
            })
            .slice(0, 8)
    }, [data.patients, index.summaries, query])

    return (
        <CommandPrimitive shouldFilter={false} loop className='flex min-h-0 flex-1 flex-col'>
            <div className='border-b px-5 pt-5 pb-4 pr-12'>
                <DialogTitle className='flex items-center gap-2 text-lg font-semibold'>
                    <copy.icon className='size-5 text-primary' />
                    {copy.title}
                </DialogTitle>

                <DialogDescription className='mt-1'>
                    {copy.description}
                </DialogDescription>

                <div className='relative mt-4'>
                    <Search className='pointer-events-none absolute top-1/2 left-3.5 size-5 -translate-y-1/2 text-muted-foreground' />

                    <CommandPrimitive.Input
                        autoFocus
                        value={query}
                        onValueChange={setQuery}
                        placeholder='Name, phone number or patient ID'
                        className='h-12 w-full rounded-xl border border-input bg-background pr-4 pl-11 text-base outline-none transition-colors placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/40'
                    />
                </div>
            </div>

            <CommandPrimitive.List className='max-h-[min(420px,55dvh)] overflow-y-auto p-2'>
                <CommandPrimitive.Empty className='flex flex-col items-center gap-3 px-4 py-10 text-center'>
                    <p className='text-sm text-muted-foreground'>
                        {query.trim() ? <>No patient matches &ldquo;{query.trim()}&rdquo;.</> : 'No patients registered yet.'}
                    </p>

                    <Button onClick={() => onRegister(query)} onKeyDown={event => event.stopPropagation()}>
                        <UserPlus />
                        Register as new patient
                    </Button>
                </CommandPrimitive.Empty>

                {results.length > 0 && (
                    <CommandPrimitive.Group
                        heading={query.trim() ? `${results.length} matching ${results.length === 1 ? 'patient' : 'patients'}` : 'Recent patients'}
                        className='**:[[cmdk-group-heading]]:px-3 **:[[cmdk-group-heading]]:pt-2 **:[[cmdk-group-heading]]:pb-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-muted-foreground'
                    >
                        {results.map(patient => {
                            const summary = index.summaries.get(patient.id) ?? EMPTY_SUMMARY
                            const lastVisit = summary.lastConsultationAt
                                ? `Last visit ${relativeDay(toDayKey(summary.lastConsultationAt), today).toLowerCase()}`
                                : `Registered ${formatDate(patient.createdAt)}`

                            return (
                                <CommandPrimitive.Item
                                    key={patient.id}
                                    value={patient.id}
                                    onSelect={() => onSelect(patient, mode)}
                                    className='group flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 outline-none data-[selected=true]:bg-accent'
                                >
                                    <PatientAvatar name={patient.name} seed={patient.id} />

                                    <div className='flex min-w-0 flex-1 flex-col'>
                                        <span className='truncate text-sm font-semibold text-foreground'>
                                            {patient.name}
                                        </span>

                                        <span className='truncate text-xs text-muted-foreground tabular-nums'>
                                            {patient.code} · {formatPhone(patient.phone)} · {ageGenderLabel(patient, today)}
                                        </span>
                                    </div>

                                    <span className='hidden text-right text-xs text-muted-foreground sm:block'>
                                        {lastVisit}
                                    </span>

                                    <span className='flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-primary opacity-0 transition-opacity group-data-[selected=true]:opacity-100'>
                                        {copy.action}
                                        <ArrowRight className='size-3.5' />
                                    </span>
                                </CommandPrimitive.Item>
                            )
                        })}
                    </CommandPrimitive.Group>
                )}
            </CommandPrimitive.List>

            <div className='flex flex-wrap items-center justify-between gap-2 border-t bg-muted/40 px-5 py-3'>
                <p className='text-xs text-muted-foreground'>
                    Use arrow keys and Enter to choose.
                </p>

                <Button variant='outline' size='sm' onClick={() => onRegister(query)} onKeyDown={event => event.stopPropagation()}>
                    <UserPlus />
                    Register new patient
                </Button>
            </div>
        </CommandPrimitive>
    )
}

const PatientSearchDialog: FC<PatientSearchDialogProps> = ({ open, onOpenChange, ...props }): ReactNode => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='top-[12dvh] flex max-h-[calc(100dvh-14dvh)] translate-y-0 flex-col gap-0 overflow-hidden p-0 sm:max-w-xl'>
                <SearchPanel {...props} />
            </DialogContent>
        </Dialog>
    )
}

export default PatientSearchDialog
